import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientForm from "@/pages/PatientForm";
import PatientRecord from "@/pages/PatientRecord";
import RecordsPage from "@/pages/RecordsPage";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import Employees from "@/pages/Employees";
import Roles from "@/pages/Roles";
import Locations from "@/pages/Locations";
import Assignments from "@/pages/Assignments";
import ImportExport from "@/pages/ImportExport";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Account from "@/pages/Account";
import ClinicalManagement from "@/pages/ClinicalManagement";
import TestingRotation from "@/pages/TestingRotation";
import MedicationRequests from "@/pages/MedicationRequests";

function Home() {
  const { can } = useApp();
  if (can("page:dashboard")) return <Dashboard />;
  const to = [["page:testing_rotation", "/testing-rotation"], ["page:medication_requests", "/medication-requests"], ["page:notifications", "/notifications"]].find(([p]) => can(p));
  return to ? <Navigate to={to[1]} replace /> : <Navigate to="/account" replace />;
}

function Guard({ perm, children }) {
  const { user, can } = useApp();
  if (user === null) return <div className="p-10 text-sm text-[var(--muted-ink)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (perm && !can(perm) && !(perm.startsWith("page:") && user.perms?.[perm])) return <Layout><div className="panel p-10" data-testid="access-denied">You do not have access to this page.</div></Layout>;
  return <Layout>{children}</Layout>;
}

const R = (path, perm, el) => <Route key={path} path={path} element={<Guard perm={perm}>{el}</Guard>} />;

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {R("/", null, <Home />)}
          {R("/patients", "page:patients", <Patients />)}
          {R("/my-dashboard", "page:dashboard", <Dashboard mine />)}
          {R("/my-patients", "page:patients", <Patients mine />)}
          {R("/patients/new", "action:patient_create", <PatientForm />)}
          {R("/patients/:id", "page:patients", <PatientRecord />)}
          {R("/patients/:id/edit", "action:patient_edit", <PatientForm />)}
          {R("/clinical", null, <ClinicalManagement />)}
          {R("/appointments", "page:appointments", <Navigate to="/clinical?tab=appointments" replace />)}
          {R("/prescriptions", "page:prescriptions", <Navigate to="/clinical?tab=prescriptions" replace />)}
          {R("/injectables", "page:injectables", <Navigate to="/clinical?tab=injectables" replace />)}
          {R("/labs", "page:labs", <Navigate to="/clinical?tab=labs" replace />)}
          {R("/tasks", "page:tasks", <RecordsPage kind="tasks" />)}
          {R("/testing-rotation", "page:testing_rotation", <TestingRotation />)}
          {R("/medication-requests", "page:medication_requests", <MedicationRequests />)}
          {R("/notifications", "page:notifications", <Notifications />)}
          {R("/reports", "page:reports", <Reports />)}
          {R("/employees", "page:employees", <Employees />)}
          {R("/roles", "page:roles", <Roles />)}
          {R("/locations", "page:locations", <Locations />)}
          {R("/assignments", "page:assignments", <Assignments />)}
          {R("/import", "page:import_export", <ImportExport />)}
          {R("/audit", "page:audit", <Audit />)}
          {R("/settings", "page:settings", <Settings />)}
          {R("/account", null, <Account />)}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AppProvider>
  );
}
